import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Upload, Camera, X, AlertCircle, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Alert, AlertDescription } from "../components/ui/alert";
import { useAuth } from "../App";
import apiClient from "../lib/api";
import { toastApiError } from "../lib/toastApiError";
import { FormPageShell } from "../components/FormPageShell";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_97109381-3560-4331-a0e4-28136ead7f0e/artifacts/z6ijff3c_logo1.png";

const RegisterPage = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { t } = useTranslation("auth");
  const { t: tc } = useTranslation("common");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // GPS
  const [gpsCoords, setGpsCoords] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    entity_name: "",
    entity_type: "",
    entity_type_other: "",
    address: "",
    city: "",
    commune: "",
    country: "RDC",
    kam_code: "",
  });

  // Files
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [identityDoc, setIdentityDoc] = useState(null);
  const [identityPreview, setIdentityPreview] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (type, file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      if (type === "photo") {
        setPhoto(file);
        setPhotoPreview(e.target.result);
      } else {
        setIdentityDoc(file);
        setIdentityPreview(e.target.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const validateStep1 = () => {
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      toast.error(t("fillAllFields"));
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error(t("passwordsMismatch"));
      return false;
    }
    if (formData.password.length < 6) {
      toast.error(t("passwordMinLength"));
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.entity_name || !formData.entity_type || !formData.address) {
      toast.error(t("fillRequiredFields"));
      return false;
    }
    if (formData.entity_type === "autre" && !formData.entity_type_other) {
      toast.error(t("specifyEntityType"));
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!photo || !identityDoc) {
      toast.error(t("uploadBothDocs"));
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
      // Auto-detect GPS when entering step 2
      detectGPS();
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const detectGPS = () => {
    if (!navigator.geolocation) {
      setGpsError(t("gpsNotSupported"));
      return;
    }
    setGpsLoading(true);
    setGpsError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsCoords({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setGpsLoading(false);
        toast.success(t("gpsSuccess"));
      },
      () => {
        setGpsLoading(false);
        setGpsError(t("gpsFailed"));
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const handleSubmit = async () => {
    if (!validateStep3()) return;

    setLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("email", formData.email);
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append("password", formData.password);
      formDataToSend.append("entity_name", formData.entity_name);
      formDataToSend.append("entity_type", formData.entity_type);
      formDataToSend.append("entity_type_other", formData.entity_type_other || "");
      formDataToSend.append("address", formData.address);
      formDataToSend.append("city", formData.city);
      formDataToSend.append("commune", formData.commune || "");
      formDataToSend.append("country", formData.country);
      if (gpsCoords) {
        formDataToSend.append("latitude", gpsCoords.lat);
        formDataToSend.append("longitude", gpsCoords.lng);
      }
      formDataToSend.append("photo", photo);
      formDataToSend.append("identity_doc", identityDoc);
      if (formData.kam_code?.trim()) {
        formDataToSend.append("kam_code", formData.kam_code.trim());
      }

      const response = await apiClient.post("/auth/register", formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      localStorage.setItem("omu_token", response.data.access_token);
      setUser(response.data.user);

      toast.success(t("registerProSuccess"));
      navigate("/dashboard");
    } catch (error) {
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormPageShell centered={false}>
      <div className="max-w-lg mx-auto w-full">
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="OMU" className="h-20 w-auto mx-auto" />
          </Link>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mt-4">
            {t("registerProTitle")}
          </h1>
          <p className="text-slate-500 mt-2">
            {t("registerProSubtitle")}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`flex items-center ${s < 3 ? "flex-1" : ""}`}
            >
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                  step >= s
                    ? "bg-omu-blue-500 text-white"
                    : "bg-slate-200 text-slate-500"
                }`}
              >
                {s}
              </div>
              {s < 3 && (
                <div
                  className={`flex-1 h-1 mx-2 rounded ${
                    step > s ? "bg-omu-blue-500" : "bg-slate-200"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        <Card className="border-0 shadow-soft">
          <CardContent className="p-6">
            {/* Step 1: Account Info */}
            {step === 1 && (
              <div className="space-y-4" data-testid="register-step-1">
                <h2 className="font-heading text-lg font-semibold">
                  {t("stepAccountInfo")}
                </h2>

                <div className="space-y-2">
                  <Label htmlFor="email">{t("email")} *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder={t("emailPlaceholder")}
                    value={formData.email}
                    onChange={handleInputChange}
                    className="h-12 rounded-xl"
                    data-testid="register-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">{t("phoneOptional")}</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder={t("phonePlaceholder")}
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="h-12 rounded-xl"
                    data-testid="register-phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t("password")} *</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder={t("passwordPlaceholder")}
                      value={formData.password}
                      onChange={handleInputChange}
                      className="h-12 rounded-xl pr-12"
                      data-testid="register-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t("confirmPassword")} *</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="h-12 rounded-xl"
                    data-testid="register-confirm-password-input"
                  />
                </div>

                <Button
                  className="w-full h-12 rounded-full bg-omu-blue-500 hover:bg-omu-blue-600"
                  onClick={handleNext}
                  data-testid="register-next-btn-1"
                >
                  {t("continue")}
                </Button>
              </div>
            )}

            {/* Step 2: Entity Info */}
            {step === 2 && (
              <div className="space-y-4" data-testid="register-step-2">
                <h2 className="font-heading text-lg font-semibold">
                  {t("stepEntityInfo")}
                </h2>

                <div className="space-y-2">
                  <Label htmlFor="entity_name">{t("entityName")}</Label>
                  <Input
                    id="entity_name"
                    name="entity_name"
                    type="text"
                    placeholder={t("entityNamePlaceholder")}
                    value={formData.entity_name}
                    onChange={handleInputChange}
                    className="h-12 rounded-xl"
                    data-testid="register-entity-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="entity_type">{t("entityType")}</Label>
                  <Select
                    value={formData.entity_type}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, entity_type: value }))
                    }
                  >
                    <SelectTrigger className="h-12 rounded-xl" data-testid="register-entity-type-select">
                      <SelectValue placeholder={t("selectPlaceholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pharmacie">{t("entityTypePharmacy")}</SelectItem>
                      <SelectItem value="clinique">{t("entityTypeClinic")}</SelectItem>
                      <SelectItem value="cabinet">{t("entityTypeCabinet")}</SelectItem>
                      <SelectItem value="autre">{t("entityTypeOther")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="kam_code">{t("kamCode")}</Label>
                  <Input
                    id="kam_code"
                    name="kam_code"
                    type="text"
                    placeholder={t("kamCodePlaceholder")}
                    value={formData.kam_code}
                    onChange={handleInputChange}
                    className="h-12 rounded-xl uppercase"
                    data-testid="register-kam-code-input"
                  />
                  <p className="text-xs text-slate-500">
                    {t("kamCodeHint")}
                  </p>
                </div>

                {formData.entity_type === "autre" && (
                  <div className="space-y-2">
                    <Label htmlFor="entity_type_other">{t("specify")}</Label>
                    <Input
                      id="entity_type_other"
                      name="entity_type_other"
                      type="text"
                      placeholder={t("specifyPlaceholder")}
                      value={formData.entity_type_other}
                      onChange={handleInputChange}
                      className="h-12 rounded-xl"
                      data-testid="register-entity-type-other-input"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="address">{t("address")}</Label>
                  <Input
                    id="address"
                    name="address"
                    type="text"
                    placeholder={t("addressPlaceholder")}
                    value={formData.address}
                    onChange={handleInputChange}
                    className="h-12 rounded-xl"
                    data-testid="register-address-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">{t("cityOptional")}</Label>
                    <Input
                      id="city"
                      name="city"
                      type="text"
                      placeholder={t("cityPlaceholder")}
                      value={formData.city}
                      onChange={handleInputChange}
                      className="h-12 rounded-xl"
                      data-testid="register-city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="commune">{t("commune")}</Label>
                    <Input
                      id="commune"
                      name="commune"
                      type="text"
                      placeholder={t("communePlaceholder")}
                      value={formData.commune}
                      onChange={handleInputChange}
                      className="h-12 rounded-xl"
                      data-testid="register-commune-input"
                    />
                  </div>
                </div>

                {/* GPS Location */}
                <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="gps-section">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2 text-sm font-medium">
                      <MapPin className="h-4 w-4 text-omu-blue-500" />
                      {t("gpsPosition")}
                    </Label>
                    {gpsCoords ? (
                      <Badge className="bg-green-100 text-green-700">{t("gpsDetected")}</Badge>
                    ) : gpsLoading ? (
                      <Badge className="bg-amber-100 text-amber-700">{t("gpsDetecting")}</Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-600">{t("gpsNotDetected")}</Badge>
                    )}
                  </div>
                  
                  {gpsCoords && (
                    <p className="text-xs text-slate-500">
                      Lat: {gpsCoords.lat.toFixed(6)}, Lng: {gpsCoords.lng.toFixed(6)}
                    </p>
                  )}
                  
                  {gpsError && (
                    <p className="text-xs text-red-500">{gpsError}</p>
                  )}
                  
                  {!gpsCoords && !gpsLoading && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full rounded-xl"
                      onClick={detectGPS}
                      data-testid="detect-gps-btn"
                    >
                      <MapPin className="h-4 w-4 mr-2" />
                      {t("detectPosition")}
                    </Button>
                  )}
                  
                  {gpsLoading && (
                    <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t("detecting")}
                    </div>
                  )}
                  
                  <p className="text-xs text-slate-400">
                    {t("gpsHint")}
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1 h-12 rounded-full"
                    onClick={() => setStep(1)}
                    data-testid="register-back-btn-2"
                  >
                    {t("back")}
                  </Button>
                  <Button
                    className="flex-1 h-12 rounded-full bg-omu-blue-500 hover:bg-omu-blue-600"
                    onClick={handleNext}
                    data-testid="register-next-btn-2"
                  >
                    {t("continue")}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Verification Documents */}
            {step === 3 && (
              <div className="space-y-4" data-testid="register-step-3">
                <h2 className="font-heading text-lg font-semibold">
                  {t("stepVerificationDocs")}
                </h2>

                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {t("docsVerificationAlert")}
                  </AlertDescription>
                </Alert>

                {/* Photo Upload */}
                <div className="space-y-2">
                  <Label>{t("yourPhoto")}</Label>
                  <div
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                      photoPreview
                        ? "border-omu-blue-500 bg-omu-blue-50"
                        : "border-slate-200 hover:border-omu-blue-500"
                    }`}
                    onClick={() => document.getElementById("photo-input").click()}
                    data-testid="photo-upload-zone"
                  >
                    {photoPreview ? (
                      <div className="relative">
                        <img
                          src={photoPreview}
                          alt="Photo preview"
                          className="w-32 h-32 object-cover rounded-full mx-auto"
                        />
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-0 right-1/4 rounded-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPhoto(null);
                            setPhotoPreview(null);
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Camera className="h-10 w-10 mx-auto text-slate-400" />
                        <p className="text-slate-600 font-medium mt-2">{t("photoResponsible")}</p>
                        <p className="text-sm text-slate-400">{t("clickToSelect")}</p>
                      </>
                    )}
                    <input
                      id="photo-input"
                      type="file"
                      accept="image/*"
                      capture="user"
                      className="hidden"
                      onChange={(e) => handleFileChange("photo", e.target.files[0])}
                      data-testid="photo-input"
                    />
                  </div>
                </div>

                {/* Identity Document Upload */}
                <div className="space-y-2">
                  <Label>{t("identityDoc")}</Label>
                  <div
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                      identityPreview
                        ? "border-omu-blue-500 bg-omu-blue-50"
                        : "border-slate-200 hover:border-omu-blue-500"
                    }`}
                    onClick={() => document.getElementById("identity-input").click()}
                    data-testid="identity-upload-zone"
                  >
                    {identityPreview ? (
                      <div className="relative">
                        <img
                          src={identityPreview}
                          alt="Identity preview"
                          className="max-h-40 mx-auto rounded-lg"
                        />
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-0 right-0 rounded-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIdentityDoc(null);
                            setIdentityPreview(null);
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Upload className="h-10 w-10 mx-auto text-slate-400" />
                        <p className="text-slate-600 font-medium mt-2">
                          {t("identityCardOrPassport")}
                        </p>
                        <p className="text-sm text-slate-400">{t("clickToSelect")}</p>
                      </>
                    )}
                    <input
                      id="identity-input"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleFileChange("identity", e.target.files[0])}
                      data-testid="identity-input"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1 h-12 rounded-full"
                    onClick={() => setStep(2)}
                    data-testid="register-back-btn-3"
                  >
                    {t("back")}
                  </Button>
                  <Button
                    className="flex-1 h-12 rounded-full bg-omu-blue-500 hover:bg-omu-blue-600"
                    onClick={handleSubmit}
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      t("createMyAccount")
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-500 mt-6">
          {t("alreadySignedIn")}{" "}
          <Link to="/login" className="text-omu-blue-500 font-medium hover:underline">
            {t("signIn")}
          </Link>
        </p>

        <p className="text-center text-sm text-slate-400 mt-4">
          <Link to="/" className="hover:text-slate-600">
            {tc("backHome")}
          </Link>
        </p>
      </div>
    </FormPageShell>
  );
};

export default RegisterPage;
